import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { useEffect } from "react";
import { 
  ArrowLeft, 
  Download, 
  Upload, 
  Database, 
  FileSpreadsheet,
  Calendar,
  Users,
  Shield,
  Mail,
  RefreshCw
} from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";
import FeatureGate from "@/components/FeatureGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataBackupPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeBackup, setActiveBackup] = useState(null); // null | "json" | "zip" — tracks which of the two Complete Backup buttons is running
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [weeklyStatus, setWeeklyStatus] = useState(null);
  const [savingOptOut, setSavingOptOut] = useState(false);

  useEffect(() => {
    const loadWeeklyStatus = async () => {
      try {
        const resp = await axios.get(`${API}/weekly-backup/status`);
        setWeeklyStatus(resp.data);
      } catch {
        // silently ignore — UI just hides the panel
      }
    };
    loadWeeklyStatus();
  }, []);

  const handleOptOutToggle = async (next) => {
    setSavingOptOut(true);
    try {
      await axios.post(`${API}/weekly-backup/opt-out`, { optOut: next });
      setWeeklyStatus((prev) => (prev ? { ...prev, optOut: next } : prev));
      toast.success(next ? "Weekly backup email turned OFF" : "Weekly backup email turned ON");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update preference");
    }
    setSavingOptOut(false);
  };

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleExportChildrenExcel = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/export/children/excel`, {
        responseType: 'blob'
      });
      
      const filename = response.headers['content-disposition']?.split('filename=')[1] || 'children_backup.xlsx';
      downloadFile(response.data, filename);
      toast.success("Children data exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export children data");
    }
    setLoading(false);
  };

  const handleExportChildrenJSON = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/export/children`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const date = new Date().toISOString().split('T')[0];
      downloadFile(blob, `children_backup_${date}.json`);
      toast.success("Children data exported as JSON!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export children data");
    }
    setLoading(false);
  };

  const handleExportSignInsExcel = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select a date range");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/export/signins/excel?start_date=${startDate}&end_date=${endDate}`,
        { responseType: 'blob' }
      );
      
      const filename = response.headers['content-disposition']?.split('filename=')[1] || 'signins_backup.xlsx';
      downloadFile(response.data, filename);
      toast.success("Sign-in records exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export sign-in records");
    }
    setLoading(false);
  };

  const handleExportSignInsJSON = async () => {
    setLoading(true);
    try {
      let url = `${API}/export/signins`;
      if (startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      }
      
      const response = await axios.get(url);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const date = new Date().toISOString().split('T')[0];
      downloadFile(blob, `signins_backup_${date}.json`);
      toast.success("Sign-in records exported as JSON!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export sign-in records");
    }
    setLoading(false);
  };

  const handleFullBackup = async () => {
    if (activeBackup) return; // one backup at a time
    setActiveBackup("json");
    try {
      const response = await axios.get(`${API}/export/full-backup`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const date = new Date().toISOString().split('T')[0];
      downloadFile(blob, `full_backup_${date}.json`);
      toast.success("Full backup created successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to create full backup");
    }
    setActiveBackup(null);
  };

  const handleDocumentsZip = async () => {
    if (activeBackup) return; // one backup at a time
    setActiveBackup("zip");
    try {
      toast.info("Bundling documents — this may take up to a minute…");
      const response = await axios.get(`${API}/export/documents-zip`, {
        responseType: 'blob',
        timeout: 300000, // 5 minutes
      });
      const filename =
        response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') ||
        `documents_${new Date().toISOString().split('T')[0]}.zip`;
      downloadFile(response.data, filename);
      toast.success("All documents downloaded as ZIP!");
    } catch (error) {
      console.error("ZIP export error:", error);
      toast.error("Failed to download documents ZIP");
    }
    setActiveBackup(null);
  };

  const handleImportChildren = async () => {
    if (!importFile) {
      toast.error("Please select a JSON file to import");
      return;
    }

    setLoading(true);
    try {
      const fileContent = await importFile.text();
      const data = JSON.parse(fileContent);
      
      const response = await axios.post(`${API}/import/children`, data);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import data");
    }
    setLoading(false);
  };

  // Set default date range to current month
  const setCurrentMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  };

  const setLastMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  };

  return (
    <AppShell pageTitle="Data Backup & Export" breadcrumb>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.backup}
        title="Backup & Restore"
        subtitle="Securely backup and export your childcare data"
        altText="Cloud data backup and security"
      />
      
      <FeatureGate feature="full_backup">
      <div className="relative z-10">
        {/* Header Info */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-brand-highlight/10 rounded-xl">
            <Database className="w-8 h-8 text-brand-highlight" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-ink-main">Data Backup & Export</h2>
            <p className="text-sm text-ink-muted">Download your data to keep it safe</p>
          </div>
        </div>

        {/* Full Backup Card */}
        <Card className="mb-6 border-2 border-success/30 bg-success/5 shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-success">
              <Database className="w-6 h-6" />
              Complete Backup
            </CardTitle>
            <CardDescription>
              Download ALL your data in one file (children, sign-ins, staff records, settings)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              onClick={handleFullBackup}
              disabled={activeBackup !== null}
              data-testid="full-backup-json-btn"
              className="bg-success hover:bg-success/90 text-white rounded-xl"
            >
              {activeBackup === "json" ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {activeBackup === "json" ? "Downloading Full Backup…" : "Download Full Backup (JSON)"}
            </Button>
            <div>
              <Button
                onClick={handleDocumentsZip}
                disabled={activeBackup !== null}
                data-testid="documents-zip-btn"
                variant="outline"
                className="border-success text-success hover:bg-success/10 rounded-xl"
              >
                {activeBackup === "zip" ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {activeBackup === "zip" ? "Downloading Documents ZIP…" : "Download All Documents (ZIP)"}
              </Button>
              <p className="text-xs text-ink-muted mt-2">
                Includes staff clearance docs, communication attachments, menus, and child photos.
              </p>
              {activeBackup && (
                <p className="text-xs text-success font-medium mt-2" data-testid="backup-in-progress-note">
                  {activeBackup === "json" ? "Full Backup (JSON)" : "Documents ZIP"} is downloading — please wait for it to finish before starting another.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Weekly Automated Backup Status */}
        {weeklyStatus && (
          <Card className="mb-6 border border-line shadow-card" data-testid="weekly-backup-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-brand-primary">
                <Mail className="w-6 h-6" />
                Automated Weekly Backup
              </CardTitle>
              <CardDescription>
                Every Monday at 8:00 AM Eastern Time, we email a secure, single-use download
                link to <strong>{weeklyStatus.ownerEmail || "your owner email"}</strong>. The
                email itself never contains your data.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <div className="text-xs text-ink-muted uppercase tracking-wide">Next email</div>
                  <div className="font-semibold text-ink-main">
                    {weeklyStatus.nextRunAt
                      ? new Date(weeklyStatus.nextRunAt).toLocaleString()
                      : "Monday 8:00 AM ET"}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <div className="text-xs text-ink-muted uppercase tracking-wide">Last sent</div>
                  <div className="font-semibold text-ink-main">
                    {weeklyStatus.lastSentAt
                      ? new Date(weeklyStatus.lastSentAt).toLocaleString()
                      : "Never (first run is coming)"}
                  </div>
                </div>
              </div>

              {(user?.role === "owner" || user?.role === "co-owner") && (
                <div className="flex items-center justify-between pt-3 border-t">
                  <div>
                    <div className="font-medium text-ink-main">Receive weekly backup emails</div>
                    <p className="text-xs text-ink-muted">
                      Turn off if you'd rather download manually whenever you like.
                    </p>
                  </div>
                  <Switch
                    checked={!weeklyStatus.optOut}
                    disabled={savingOptOut}
                    onCheckedChange={(checked) => handleOptOutToggle(!checked)}
                    data-testid="weekly-backup-toggle"
                  />
                </div>
              )}

              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                <strong>Security:</strong> each link is single-use, expires in 72 hours, and
                requires you to be signed in as the owner. The email is sent only to your owner
                email — please keep it confidential.
              </div>
            </CardContent>
          </Card>
        )}

        {/* Children Export Card */}
        <Card className="mb-6 border border-line shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-brand-primary">
              <Users className="w-6 h-6" />
              Export Children Data
            </CardTitle>
            <CardDescription>
              Download all children with their pickup persons and categories
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <Button
                onClick={handleExportChildrenExcel}
                disabled={loading}
                className="bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Download Excel
              </Button>
              <Button
                onClick={handleExportChildrenJSON}
                disabled={loading}
                variant="outline"
                className="border-brand-primary text-brand-primary rounded-xl"
              >
                <Download className="w-4 h-4 mr-2" />
                Download JSON
              </Button>
            </div>
            <p className="text-sm text-ink-muted">
              Excel format can be re-uploaded using Bulk Upload. JSON is for complete backup.
            </p>
          </CardContent>
        </Card>

        {/* Sign-Ins Export Card */}
        <Card className="mb-6 border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Calendar className="w-6 h-6" />
              Export Sign-In Records
            </CardTitle>
            <CardDescription>
              Download attendance records for a specific date range
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={setCurrentMonth}>
                This Month
              </Button>
              <Button variant="outline" size="sm" onClick={setLastMonth}>
                Last Month
              </Button>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleExportSignInsExcel}
                disabled={loading || !startDate || !endDate}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Download Excel
              </Button>
              <Button
                onClick={handleExportSignInsJSON}
                disabled={loading}
                variant="outline"
                className="border-blue-300"
              >
                <Download className="w-4 h-4 mr-2" />
                Download JSON
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Import Card - Owner Only */}
        {user?.role === 'owner' && (
          <Card className="mb-6 border-2 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-800">
                <Upload className="w-6 h-6" />
                Import/Restore Data
              </CardTitle>
              <CardDescription>
                Restore children from a JSON backup file
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="importFile">Select JSON Backup File</Label>
                <Input
                  id="importFile"
                  type="file"
                  accept=".json"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="mt-1"
                />
              </div>
              <Button
                onClick={handleImportChildren}
                disabled={loading || !importFile}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Import Children
              </Button>
              <p className="text-sm text-gray-500">
                Note: Existing children with the same name will be skipped (not duplicated).
              </p>
            </CardContent>
          </Card>
        )}

        {/* Tips Card */}
        <Card className="border-2 border-gray-200 bg-gray-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-700">
              <Shield className="w-6 h-6" />
              Backup Tips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2 text-sm text-ink-muted">
              <li>Download a <strong>Full Backup</strong> at least once a week</li>
              <li>Export children data <strong>before making bulk changes</strong></li>
              <li>Keep backups in a safe location (cloud storage, email to yourself)</li>
              <li>The Excel export can be re-uploaded using the Bulk Upload feature</li>
              <li>JSON backups contain all data including pickup persons</li>
            </ul>
          </CardContent>
        </Card>
      </div>
      </FeatureGate>
    </AppShell>
  );
};

export default DataBackupPage;
